import React, { useEffect } from 'react';
import { Form, Input, Button, Row, Col } from 'antd';
import { useCustomer } from '../../store/customerContext';
import { Customer } from '../../types/customer';
import { useTranslation } from "react-i18next";
import CitySelect from '../city/CitySelect';
import { useAuth } from '../../store/AuthContext';

interface CustomerFormProps {
  editCustomer?: Customer;
  onFinishEdit?: () => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ editCustomer, onFinishEdit }) => {
  const [form] = Form.useForm();
  const { addCustomer, updateCustomer } = useCustomer();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();

  const onFinish = (values: Omit<Customer, "id">) => {
    if (editCustomer) {
      updateCustomer({ ...editCustomer, ...values });
      onFinishEdit?.();
      form.resetFields();
    } else {
      const newCustomer: Customer = {
        id: Date.now(),
        ...values,
      };
      addCustomer(newCustomer);
      form.resetFields();
    }
  };

  useEffect(() => {
    const cityId = typeof editCustomer?.city === 'object' && '_id' in editCustomer.city
      ? editCustomer.city._id
      : editCustomer?.city;

    const sanitizedCustomer = {
      ...editCustomer,
      city: cityId
    };
    if (editCustomer) {
      form.setFieldsValue(sanitizedCustomer);
    }
  }, [editCustomer, form]);

  return (
    <div style={{ direction: i18n.language === 'ar' || i18n.language === 'ckb' ? 'rtl' : 'ltr' }}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label={t('name')}
              rules={[{ required: true, message: t('please_enter_name') as string }]}
            >
              <Input placeholder={t('name') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="phone1"
              label={t('phone')}
              rules={[{ required: true, message: t('please_enter_phone') as string }]}
            >
              <Input placeholder={t('phone') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="phone2"
              label={t('phone')}
              rules={[{ required: true, message: t('please_enter_phone') as string }]}
            >
              <Input placeholder={t('phone') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="city"
              label={t('city')}
              rules={[{ required: true, message: t('please_enter_city') as string }]}
            >
              <CitySelect />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="customerCode"
              label={t('customerCode')}
              rules={[{ required: true, message: t('please_enter_customerCode') as string }]}
            >
              <Input placeholder={t('customerCode') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="deliveryCost"
              label={t('deliveryCost')}
              rules={[{ message: t('please_enter_valid_delivery_cost') as string }]}
            >
              <Input type="number" placeholder={t('deliveryCost') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="address"
              label={t('address')}
              rules={[{ required: true, message: t('please_enter_address') as string }]}
            >
              <Input placeholder={t('address') as string} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                {editCustomer ? (hasPermission('customer.update') && t('update_customer')) : (hasPermission('customer.create') && t('add_customer'))}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CustomerForm;
